<template>
    <div
        class="relative flex h-[100dvh] max-h-full w-full"
        style="background-image: url('data:image/svg+xml,%3Csvg width=\'20\' height=\'20\' viewBox=\'0 0 20 20\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1h2v2H1V1zm4 0h2v2H5V1zm4 0h2v2H9V1zm4 0h2v2h-2V1zm4 0h2v2h-2V1zm-16 4h2v2H1V5zm4 0h2v2H5V5zm4 0h2v2H9V5zm4 0h2v2h-2V5zm4 0h2v2h-2V5zm-16 4h2v2H1V9zm4 0h2v2H5V9zm4 0h2v2H9V9zm4 0h2v2h-2V9zm4 0h2v2h-2V9zm-16 4h2v2H1v-2zm4 0h2v2H5v-2zm4 0h2v2H9v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2z\' fill=\'%239C92AC\' fill-opacity=\'0.05\' fill-rule=\'evenodd\'/%3E%3C/svg%3E'); box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.3), 0 0 20px 0px rgba(0, 200, 200, 0.2);"
    >
        <!-- `m-auto` rather than `justify-center` so that when the card is taller
             than the viewport it scrolls from the top instead of being clipped. -->
        <div class="absolute inset-0 flex flex-col overflow-y-auto p-4 sm:p-8">
            <div class="m-auto w-full max-w-lg shrink-0">
                <button type="button" class="mb-4 sm:mb-8" @click="returnToLogin">
                    <i class="fas fa-arrow-left text-2xl" aria-hidden="true" />
                    <span class="sr-only">Back to login</span>
                </button>

                <div class="single-account-view rounded-xl p-5 shadow-xl sm:p-8">
                    <div class="mb-5 text-center sm:mb-6">
                        <div v-if="userData?.profileImage" class="mx-auto mt-2 flex items-center justify-center p-2 text-center sm:mt-4 sm:p-4">
                            <img
                                :src="userData.profileImage"
                                :alt="`${userData?.name || 'User'} profile`"
                                class="mr-3 h-20 w-20 rounded-full object-cover sm:h-24 sm:w-24"
                            />
                        </div>

                        <template v-if="showPinStep">
                            <h2 class="mb-4 text-2xl font-bold sm:text-3xl">Enter your PIN</h2>
                            <p class="text-sm text-gray-600">
                                Enter the 6-digit PIN you set in your account security settings.
                            </p>
                        </template>
                        <template v-else-if="showSmsStep">
                            <h2 class="mb-4 text-2xl font-bold sm:text-3xl">Enter multi-factor verification code</h2>
                            <p class="text-sm text-gray-600">
                                We sent a 6-digit code to
                                <span class="font-semibold text-gray-800">{{ destinationHint }}</span>{{ codeDeliveryChannelSuffix }}.
                            </p>
                            <p class="mt-4 flex items-center justify-center py-2 text-sm text-teal-600">
                                <font-awesome-icon icon="fa-solid fa-clock" class="mr-1" />
                                Expires in: {{ formatTime(remainingTime) }}
                            </p>
                        </template>
                    </div>

                    <div class="mb-6 flex justify-center gap-2 sm:mb-8 sm:gap-3">
                        <input
                            v-for="(_, index) in code"
                            :key="index"
                            :ref="(el) => (inputRefs[index] = el)"
                            v-model="code[index]"
                            type="text"
                            inputmode="numeric"
                            pattern="[0-9]*"
                            maxlength="1"
                            class="h-14 w-full min-w-0 max-w-[3.25rem] transform rounded-xl border-2 border-gray-300 text-center text-2xl font-bold shadow-md transition duration-300 hover:scale-105 hover:shadow-lg focus:border-teal-600 focus:ring-4 focus:ring-teal-300 focus:ring-opacity-50 sm:h-16 sm:max-w-[4rem] sm:border-3 sm:text-4xl"
                            @input="handleInput(index, $event)"
                            @keydown="handleKeyDown(index, $event)"
                            @paste="handlePaste($event)"
                        />
                    </div>

                    <div v-if="verificationError" class="mb-4 text-center text-sm text-red-800" role="alert">
                        {{ verificationError }}
                    </div>

                    <button
                        type="button"
                        class="w-full rounded-lg bg-teal-600 py-3 px-5 text-lg font-semibold text-white shadow-md transition duration-200 hover:bg-teal-700 sm:py-4 sm:px-6 sm:text-xl"
                        :disabled="isVerifying"
                        @click="submitCode"
                    >
                        <font-awesome-icon icon="fa-duotone fa-solid fa-badge-check" class="mr-2" />
                        {{ isVerifying ? 'Verifying…' : (showPinStep ? 'Verify PIN' : 'Submit code') }}
                    </button>

                    <div v-if="bothMethodsConfigured && showPinStep" class="mt-4 text-center">
                        <button
                            type="button"
                            class="text-sm font-medium text-teal-600 hover:underline"
                            :disabled="isVerifying || smsSwitchLoading"
                            @click="switchToSmsStep"
                        >
                            {{ smsSwitchLoading ? 'Sending text…' : 'Try another way (text message)' }}
                        </button>
                    </div>

                    <div v-if="bothMethodsConfigured && showSmsStep" class="mt-4 text-center">
                        <button
                            type="button"
                            class="text-sm font-medium text-teal-600 hover:underline"
                            :disabled="isVerifying"
                            @click="switchToPinStep"
                        >
                            Use PIN instead
                        </button>
                    </div>

                    <div class="mt-6 text-center sm:mt-8">
                        <a href="#" class="font-medium text-black hover:underline" @click.prevent="returnToLogin">
                            <font-awesome-icon icon="arrow-left" class="mr-1" />
                            Return to login
                        </a>
                    </div>
                </div>

                <footer class="mt-6 text-center text-xs text-gray-500 sm:mt-8 sm:text-sm">
                    <p>©2024 All Rights Reserved. RefReps, LLC is a registered trademark.</p>
                    <p>
                        <a href="https://www.refreps.com/terms-conditions" target="_blank" class="text-teal-600 hover:underline">Privacy</a>
                        ·
                        <a href="https://www.refreps.com/terms-conditions" target="_blank" class="text-teal-600 hover:underline">Terms</a>
                    </p>
                </footer>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, nextTick, watch } from 'vue'
import axios from 'axios'
import { useVerificationCode } from '@/composables/useVerificationCode'
import { useTimer } from '@/composables/useTimer'
import { maskNumber, maskEmail, formatTime } from '@/utils/formatUtils'

/**
 * Infer whether PIN and/or SMS verification are available from login MFA snapshot
 * (twoFactorAuthentication + deliveryMethod from login).
 */
function getMfaDeliveryAvailability (loginSnapshot) {
    const u = loginSnapshot && typeof loginSnapshot === 'object' ? loginSnapshot : {}
    const tfa = u.twoFactorAuthentication || {}
    const dm = u.deliveryMethod

    let pin = tfa.hasPin === true
    let sms =
        tfa.method === 'sms' ||
        !!(String(tfa.contactValue ?? tfa.contact_value ?? '').trim())

    if (dm === 'pin') {
        pin = true
    }
    if (dm === 'sms') {
        sms = true
    }

    if (!pin && !sms) {
        if (dm === 'pin') {
            return { pin: true, sms: false, both: false }
        }
        return { pin: false, sms: true, both: false }
    }

    return { pin, sms, both: pin && sms }
}

export default defineComponent({
    name: 'MultiFactorVerification',
    props: {
        challengeId: {
            type: String,
            default: null,
        },
        pendingUser: {
            type: Object,
            default: null,
        },
        deliveryMethod: {
            type: String,
            default: null,
        },
        companyID: {
            type: String,
            default: '',
        },
        enrollLink: {
            type: String,
            default: '',
        },
    },
    emits: ['back-to-home', 'verified'],
    setup (props, { emit }) {
        const { remainingTime, startTimer, stopTimer } = useTimer(600)

        const { code, inputRefs, handleInput, handleKeyDown, handlePaste } = useVerificationCode()

        const isVerifying = ref(false)
        const smsSwitchLoading = ref(false)
        const verificationError = ref('')
        const verificationChannel = ref('sms')
        const userData = computed(() => props.pendingUser || {})

        const availability = computed(() => getMfaDeliveryAvailability(userData.value))

        const pinAvailable = computed(() => availability.value.pin)
        const smsAvailable = computed(() => availability.value.sms)
        const bothMethodsConfigured = computed(() => availability.value.both)

        /** Active step: PIN first when both methods exist; otherwise the only configured method. */
        const showPinStep = computed(() => {
            if (!pinAvailable.value) {
                return false
            }
            if (!smsAvailable.value) {
                return true
            }
            return verificationChannel.value === 'pin'
        })

        const showSmsStep = computed(() => {
            if (!smsAvailable.value) {
                return false
            }
            if (!pinAvailable.value) {
                return true
            }
            return verificationChannel.value === 'sms'
        })

        function clearDigits () {
            for (let i = 0; i < code.length; i++) {
                code[i] = ''
            }
        }

        function syncTimerToStep () {
            if (showSmsStep.value) {
                startTimer()
            } else {
                stopTimer()
            }
        }

        function initChannelFromUser () {
            const u = props.pendingUser || {}
            const { pin, sms } = getMfaDeliveryAvailability(u)
            if (pin && sms) {
                verificationChannel.value = 'pin'
            } else if (pin) {
                verificationChannel.value = 'pin'
            } else {
                verificationChannel.value = 'sms'
            }
        }

        watch(
            () => props.pendingUser,
            () => {
                initChannelFromUser()
                clearDigits()
                verificationError.value = ''
                syncTimerToStep()
            },
            { deep: true }
        )

        const destinationHint = computed(() => {
            const tfa = userData.value?.twoFactorAuthentication || {}
            const raw = tfa.contactValue ?? tfa.contact_value
            if (raw && String(raw).trim() !== '') {
                return String(raw)
            }
            const m = tfa.method
            if (m === 'sms' || m === 'phone') {
                return maskNumber(userData.value?.phone || '')
            }
            return maskEmail(userData.value?.email || '')
        })

        const codeDeliveryChannelSuffix = computed(() => {
            const tfa = userData.value?.twoFactorAuthentication || {}
            const m = props.deliveryMethod || tfa.method || userData.value?.deliveryMethod
            if (m === 'sms' || m === 'phone') {
                return ' (SMS)'
            }
            if (m === 'email') {
                return ' (email)'
            }
            return ' (SMS)'
        })

        const returnToLogin = () => {
            emit('back-to-home')
        }

        /** Ask backend to send (or resend) SMS for this login MFA challenge — see POST …/login-mfa-send-sms */
        async function switchToSmsStep () {
            verificationError.value = ''
            const email = userData.value?.email
            const uid = userData.value?.id
            if (!email || uid == null || uid === '') {
                verificationError.value = 'Missing account information. Please return to login.'
                return
            }

            smsSwitchLoading.value = true
            try {
                await axios.post(
                    `${import.meta.env.VITE_BASE_LINK}/login-mfa-send-sms`,
                    {
                        email,
                        userId: uid,
                        challengeId: props.challengeId || undefined,
                        ...(props.companyID != null && String(props.companyID).trim() !== ''
                            ? { companyID: props.companyID }
                            : {}),
                        ...(props.enrollLink != null && String(props.enrollLink).trim() !== ''
                            ? { enrollLink: props.enrollLink }
                            : {}),
                    },
                    { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
                )
                verificationChannel.value = 'sms'
                clearDigits()
                await nextTick()
                syncTimerToStep()
                inputRefs.value[0]?.focus()
            } catch (error) {
                verificationError.value =
                    error.response?.data?.message ||
                    'Could not send a text message. Please try again or use your PIN.'
            } finally {
                smsSwitchLoading.value = false
            }
        }

        async function switchToPinStep () {
            verificationError.value = ''
            verificationChannel.value = 'pin'
            clearDigits()
            stopTimer()
            await nextTick()
            inputRefs.value[0]?.focus()
        }

        const submitCode = async () => {
            const enteredCode = code.join('')
            if (enteredCode.length !== 6 || !/^\d+$/.test(enteredCode)) {
                verificationError.value = showPinStep.value
                    ? 'Please enter your complete 6-digit PIN.'
                    : 'Please enter the complete 6-digit code.'
                return
            }

            const email = userData.value?.email
            if (!email) {
                verificationError.value = 'Missing account email. Please return to login and try again.'
                return
            }

            isVerifying.value = true
            verificationError.value = ''

            const body = {
                email,
            }
            if (showPinStep.value) {
                body.pin = enteredCode
            } else {
                body.code = enteredCode
            }
            if (props.challengeId) {
                body.challengeId = props.challengeId
            }
            const uid = userData.value?.id
            if (uid != null && uid !== '') {
                body.userId = uid
            }
            if (props.companyID != null && String(props.companyID).trim() !== '') {
                body.companyID = props.companyID
            }
            if (props.enrollLink != null && String(props.enrollLink).trim() !== '') {
                body.enrollLink = props.enrollLink
            }

            try {
                const { data } = await axios.post(
                    `${import.meta.env.VITE_BASE_LINK}/verify-login-two-factor-code`,
                    body,
                    { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
                )

                if (data.code === 200) {
                    emit('verified', data)
                    return
                }
                verificationError.value = data.message || 'Verification failed. Please try again.'
            } catch (error) {
                verificationError.value =
                    error.response?.data?.message || error.message || 'Invalid code. Please try again.'
            } finally {
                isVerifying.value = false
            }
        }

        onMounted(async () => {
            initChannelFromUser()
            syncTimerToStep()
            await nextTick()
            inputRefs.value[0]?.focus()
        })

        return {
            code,
            inputRefs,
            handleInput,
            handleKeyDown,
            handlePaste,
            userData,
            showPinStep,
            showSmsStep,
            bothMethodsConfigured,
            destinationHint,
            codeDeliveryChannelSuffix,
            remainingTime,
            formatTime,
            isVerifying,
            smsSwitchLoading,
            verificationError,
            submitCode,
            returnToLogin,
            switchToSmsStep,
            switchToPinStep,
        }
    },
})
</script>

<style scoped>
.single-account-view {
    background: #fff;
}
</style>
